import { Checkbox, Field } from '@rental/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { rememberPoint } from '../cash/drawer'
import { CASH_QUERY_KEY, loadCash } from '../cash/queries'
import { ChoiceChips } from '../components/ChoiceChips'
import {
  FIELD,
  FIELD_ROW,
  FieldPair,
  PAIRED_FIELD,
  StaffInput,
  StaffSelect,
} from '../components/ui'
import { useLocale } from '../i18n'
import { romeNow } from '../time'
import { type DocumentDraft, type FiscalMoney, kindsFor, sourcesFor, withKind } from './document'

/**
 * S6 (CMP-3, decision 3): the fiscal document a payment or a refund was
 * given, typed from what was printed. In a money form it is optional (the
 * money then waits in Fiscale); typed later, it is the form. Cash names its
 * till through its drawer; anything else names the point, remembered on the
 * device as the drawer field remembers it.
 */
export function FiscalFields({
  name,
  money,
  draft,
  onChange,
  pointFromDrawer,
  printed,
  required = false,
  missing = null,
}: {
  /** Unique on the page: the radio groups' names. */
  name: string
  money: FiscalMoney
  draft: DocumentDraft
  onChange: (draft: DocumentDraft) => void
  /** Cash: the till is the drawer's point, and the server takes it from there. */
  pointFromDrawer: boolean
  /** Typed later: the printed date and time are shown; at the counter, opened on request. */
  printed: 'shown' | 'onRequest'
  required?: boolean
  /** What the form found missing when it was sent. */
  missing?: 'point' | 'printed' | null
}) {
  const { messages: m } = useLocale()
  const copy = m.fiscal.document
  const { access } = useAccess()
  const cash = useQuery({
    queryKey: CASH_QUERY_KEY,
    queryFn: loadCash,
    enabled: can(access, 'payments.record'),
  })
  const points = cash.data?.drawers.map((drawer) => drawer.location) ?? []
  const namesPoint = draft.source === 'till' && !pointFromDrawer
  const loaded = cash.data !== undefined
  const [onlyPoint] = points

  // A point in service only: one alone is the till's without asking; one remembered
  // on the device but since taken out of service is asked for again, never sent.
  useEffect(() => {
    if (!namesPoint || !loaded) return
    if (points.some((point) => point.id === draft.locationId)) return
    const fallback = points.length === 1 ? (onlyPoint?.id ?? null) : null
    if (draft.locationId !== fallback) onChange({ ...draft, locationId: fallback })
  }, [namesPoint, loaded, points, draft, onlyPoint, onChange])

  const sources = sourcesFor(draft.kind)
  return (
    // In a well, as every money form is: its legend groups it, with no box drawn round it (UI-2g).
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 text-sm font-medium">
        {copy.legend}
        {required ? null : (
          <span className="font-regular text-text-muted"> ({m.newBooking.optional})</span>
        )}
      </legend>
      {required ? null : <p className="text-sm text-text-muted">{copy.hint}</p>}
      <ChoiceChips
        name={`${name}-kind`}
        label={copy.kind}
        options={kindsFor(money).map((kind) => ({ value: kind, label: m.fiscal.kinds[kind] }))}
        value={draft.kind}
        onChange={(kind) => onChange(withKind(draft, kind))}
      />
      {sources.length > 1 ? (
        <ChoiceChips
          name={`${name}-source`}
          label={copy.source}
          options={sources.map((source) => ({ value: source, label: m.fiscal.sources[source] }))}
          value={draft.source}
          onChange={(source) => onChange({ ...draft, source })}
        />
      ) : null}
      {draft.source === 'till' && pointFromDrawer ? (
        <p className="text-sm">{copy.pointFromDrawer}</p>
      ) : null}
      {/* Side by side only where both fit, each at least 256 px: in a half-width pane each
          takes the whole width, and the point's name is never cut short ("Scegli il punt…"). */}
      <div className={FIELD_ROW}>
        {namesPoint && points.length > 1 ? (
          <Field
            className={FIELD}
            label={copy.point}
            required
            error={missing === 'point' && draft.locationId === null ? copy.pointChoose : null}
          >
            {(control) => (
              <StaffSelect
                {...control}
                value={draft.locationId ?? ''}
                onChange={(event) => {
                  const locationId = event.target.value || null
                  if (locationId) rememberPoint(locationId)
                  onChange({ ...draft, locationId })
                }}
              >
                <option value="">{copy.pointPrompt}</option>
                {points.map((point) => (
                  <option key={point.id} value={point.id}>
                    {point.name}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
        ) : null}
        <Field className={FIELD} label={copy.number} hint={copy.numberHint} required={required}>
          {(control) => (
            <StaffInput
              {...control}
              maxLength={60}
              autoComplete="off"
              value={draft.number}
              onChange={(event) => onChange({ ...draft, number: event.target.value })}
            />
          )}
        </Field>
      </div>
      {printed === 'onRequest' ? (
        <Checkbox
          label={copy.printedLater}
          checked={draft.printed !== null}
          onChange={(event) =>
            onChange({ ...draft, printed: event.target.checked ? romeNow() : null })
          }
        />
      ) : null}
      {draft.printed ? (
        <div className={FIELD_ROW}>
          <FieldPair>
            <Field
              className={PAIRED_FIELD}
              label={copy.printedDate}
              hint={copy.printedHint}
              required
              error={missing === 'printed' ? copy.printedInvalid : null}
            >
              {(control) => (
                <StaffInput
                  {...control}
                  type="date"
                  value={draft.printed?.date ?? ''}
                  onChange={(event) =>
                    onChange({
                      ...draft,
                      printed: { date: event.target.value, time: draft.printed?.time ?? '' },
                    })
                  }
                />
              )}
            </Field>
            <Field className={PAIRED_FIELD} label={copy.printedTime} required>
              {(control) => (
                <StaffInput
                  {...control}
                  type="time"
                  value={draft.printed?.time ?? ''}
                  onChange={(event) =>
                    onChange({
                      ...draft,
                      printed: { date: draft.printed?.date ?? '', time: event.target.value },
                    })
                  }
                />
              )}
            </Field>
          </FieldPair>
        </div>
      ) : null}
    </fieldset>
  )
}
